// src/services/productInventoryService.js
import ApiService from './api'
import AppURLs from '../config/urls'

class ProductInventoryService {
  
  // 📦 Get all product inventories
  async getAllInventories(params = {}) {
    try {
      console.log('📦 Fetching all product inventories with params:', params)
      const response = await ApiService.get(AppURLs.PRODUCT_INVENTORIES.GET_ALL, params)
      console.log('✅ Product inventories fetched successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error fetching product inventories:', error)
      throw error
    }
  }

  // 📦 Get inventory by ID
  async getInventoryById(id) {
    try {
      console.log('📦 Fetching product inventory by ID:', id)
      const endpoint = ApiService.replaceUrlParams(AppURLs.PRODUCT_INVENTORIES.GET_BY_ID, { id })
      const response = await ApiService.get(endpoint)
      console.log('✅ Product inventory fetched successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error fetching product inventory:', error)
      throw error
    }
  }

  // 📦 Create single inventory assignment
  async createInventory(inventoryData) {
    try {
      console.log('📦 Creating new product inventory assignment with data:', inventoryData)
      
      // Validate required fields
      this.validateInventoryData(inventoryData)
      
      const response = await ApiService.post(AppURLs.PRODUCT_INVENTORIES.CREATE, inventoryData)
      console.log('✅ Product inventory created successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error creating product inventory:', error)
      throw error
    }
  }

  // 📦 Create multiple inventory assignments (bulk)
  async createMultipleInventories(bulkData) {
    try {
      console.log('📦 Creating multiple product inventory assignments with data:', bulkData)
      
      // Validate bulk data structure
      this.validateBulkInventoryData(bulkData)
      
      const response = await ApiService.post(AppURLs.PRODUCT_INVENTORIES.CREATE_MULTIPLE, bulkData)
      console.log('✅ Multiple product inventories created successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error creating multiple product inventories:', error)
      throw error
    }
  }

  // 📦 Update inventory
  async updateInventory(id, updateData) {
    try {
      console.log('📦 Updating product inventory:', { id, updateData })
      const endpoint = ApiService.replaceUrlParams(AppURLs.PRODUCT_INVENTORIES.UPDATE, { id })
      const response = await ApiService.patch(endpoint, updateData)
      console.log('✅ Product inventory updated successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error updating product inventory:', error)
      throw error
    }
  }

  // 📦 Delete inventory
  async deleteInventory(id) {
    try {
      console.log('📦 Deleting product inventory:', id)
      const endpoint = ApiService.replaceUrlParams(AppURLs.PRODUCT_INVENTORIES.DELETE, { id })
      const response = await ApiService.delete(endpoint)
      console.log('✅ Product inventory deleted successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error deleting product inventory:', error)
      throw error
    }
  }

  // 📦 Get unassigned products for a branch
  async getUnassignedProducts(branchId) {
    try {
      console.log('📦 Fetching unassigned products for branch:', branchId)
      const endpoint = ApiService.replaceUrlParams(AppURLs.PRODUCT_INVENTORIES.UNASSIGNED, { branchId })
      const response = await ApiService.get(endpoint)
      console.log('✅ Unassigned products fetched successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error fetching unassigned products:', error)
      throw error
    }
  }

  // 📦 Get inventories by branch
  async getInventoriesByBranch(branchId, params = {}) {
    try {
      console.log('📦 Fetching product inventories for branch:', branchId)
      const queryParams = { branch: branchId, ...params }
      const response = await ApiService.get(AppURLs.PRODUCT_INVENTORIES.GET_ALL, queryParams)
      console.log('✅ Branch product inventories fetched successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error fetching branch product inventories:', error)
      throw error
    }
  }

  // 📦 Get inventories by product
  async getInventoriesByProduct(productId, params = {}) {
    try {
      console.log('📦 Fetching inventories for product:', productId)
      const queryParams = { product: productId, ...params }
      const response = await ApiService.get(AppURLs.PRODUCT_INVENTORIES.GET_ALL, queryParams)
      console.log('✅ Product inventories fetched successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error fetching product inventories:', error)
      throw error
    }
  }

  // 📦 Get low stock alerts
  async getLowStockAlerts(branchId = null) {
    try {
      console.log('📦 Fetching low stock alerts for branch:', branchId)
      const params = branchId ? { branch: branchId } : {}
      const response = await ApiService.get(AppURLs.PRODUCT_INVENTORIES.LOW_STOCK, params)
      console.log('✅ Low stock alerts fetched successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error fetching low stock alerts:', error)
      throw error
    }
  }

  // 📦 Get inventory statistics
  async getInventoryStats(branchId = null) {
    try {
      console.log('📦 Fetching inventory statistics for branch:', branchId)
      const params = branchId ? { branch: branchId } : {}
      const response = await ApiService.get(AppURLs.PRODUCT_INVENTORIES.STATS, params)
      console.log('✅ Inventory statistics fetched successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error fetching inventory statistics:', error)
      throw error
    }
  }

  // 🔍 Search and filter utilities
  searchInventories(inventories, searchTerm) {
    if (!searchTerm) return inventories

    const term = searchTerm.toLowerCase()
    return inventories.filter(inventory => {
      const productName = inventory.product?.name || ''
      const productSku = inventory.product?.sku || ''
      const branchName = inventory.branch?.name || ''
      
      return productName.toLowerCase().includes(term) ||
             productSku.toLowerCase().includes(term) ||
             branchName.toLowerCase().includes(term)
    })
  }

  filterByStatus(inventories, status) {
    if (!status) return inventories

    return inventories.filter(inventory => {
      const threshold = inventory.product?.threshold || 10
      const currentStatus = this.getStockStatus(inventory.currentStock, threshold)
      return currentStatus === status
    })
  }

  filterByBranch(inventories, branchId) {
    if (!branchId) return inventories

    return inventories.filter(inventory => {
      const inventoryBranchId = inventory.branch?._id || inventory.branch
      return inventoryBranchId === branchId
    })
  }

  filterByCategory(inventories, categoryId) {
    if (!categoryId) return inventories

    return inventories.filter(inventory => {
      const productCategoryId = inventory.product?.category?._id || inventory.product?.category
      return productCategoryId === categoryId
    })
  }

  filterByType(inventories, type) {
    if (!type) return inventories

    return inventories.filter(inventory => {
      return inventory.product?.type === type
    })
  }

  // Get stock status
  getStockStatus(currentStock, threshold) {
    if (currentStock === 0) {
      return 'out_of_stock'
    } else if (currentStock <= threshold) {
      return 'low_stock'
    } else if (currentStock > threshold * 3) {
      return 'over_stock'
    } else {
      return 'normal'
    }
  }

  // Sort inventories
  sortInventories(inventories, sortBy = 'product.name', sortOrder = 'asc') {
    return [...inventories].sort((a, b) => {
      let valueA, valueB

      // Handle nested properties
      if (sortBy.includes('.')) {
        const keys = sortBy.split('.')
        valueA = keys.reduce((obj, key) => obj?.[key], a)
        valueB = keys.reduce((obj, key) => obj?.[key], b)
      } else {
        valueA = a[sortBy]
        valueB = b[sortBy]
      }

      // Handle string comparison
      if (typeof valueA === 'string') {
        valueA = valueA.toLowerCase()
        valueB = valueB?.toLowerCase() || ''
      }

      // Handle null/undefined values
      if (valueA == null) valueA = ''
      if (valueB == null) valueB = ''

      if (sortOrder === 'asc') {
        return valueA < valueB ? -1 : valueA > valueB ? 1 : 0
      } else {
        return valueA > valueB ? -1 : valueA < valueB ? 1 : 0
      }
    })
  }

  // 🛡️ Validation helpers
  validateInventoryData(data) {
    if (!data.branch) {
      throw new Error('Branch is required')
    }
    if (!data.product) {
      throw new Error('Product is required')
    }
    if (typeof data.currentStock !== 'undefined') {
      const stock = Number(data.currentStock)
      if (isNaN(stock) || stock < 0) {
        throw new Error('Current stock must be a positive number')
      }
    }
  }

  validateBulkInventoryData(data) {
    if (!data.branch) {
      throw new Error('Branch is required for bulk assignment')
    }
    if (!data.products || !Array.isArray(data.products) || data.products.length === 0) {
      throw new Error('Products array is required and must not be empty')
    }
    
    // Validate each product in the array
    data.products.forEach((productItem, index) => {
      if (!productItem.product) {
        throw new Error(`Product ID is required for item at index ${index}`)
      }
    })
  }

  // 📊 Utility methods
  calculateTotalValue(inventories) {
    return inventories.reduce((total, inventory) => {
      const stock = Number(inventory.currentStock) || 0
      const cost = Number(inventory.product?.cost) || 0
      return total + (stock * cost)
    }, 0)
  }

  getInventoryStats(inventories) {
    const total = inventories.length
    const outOfStock = inventories.filter(inv => (Number(inv.currentStock) || 0) === 0).length
    const lowStock = inventories.filter(inv => {
      const stock = Number(inv.currentStock) || 0
      const threshold = Number(inv.product?.threshold) || 10
      return stock > 0 && stock <= threshold
    }).length
    const normal = total - outOfStock - lowStock

    return {
      total,
      outOfStock,
      lowStock,
      normal,
      totalValue: this.calculateTotalValue(inventories)
    }
  }
}

export default new ProductInventoryService()